// src/services/providerService.js
import { 
    collection, query, orderBy, getDocs, doc, setDoc, updateDoc, 
    deleteDoc, where, serverTimestamp, runTransaction 
} from 'firebase/firestore';
import { db } from '../config/firebase';

const PROVIDERS_COL = 'providers';
const PROVIDER_DOCS_COL = 'providerDocs';
const EXPENSES_COL = 'expenses';
const COUNTER_COL = 'counters';

export const providerService = {
    /**
     * Obtiene todos los proveedores ordenados por nombre.
     */
    getAll: async () => {
        try {
            const q = query(collection(db, PROVIDERS_COL), orderBy('name'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error obteniendo proveedores:", error);
            throw error;
        }
    },

    /**
     * Guarda un proveedor (Crear o Editar).
     */
    saveProvider: async (providerData, id = null) => {
        try {
            const targetId = id || `PROV-${Date.now()}`;
            const docRef = doc(db, PROVIDERS_COL, targetId);
            
            const payload = {
                ...providerData,
                updatedAt: serverTimestamp()
            };
            
            if (!id) {
                payload.createdAt = serverTimestamp();
                await setDoc(docRef, payload);
            } else {
                await updateDoc(docRef, payload);
            }
            
            return targetId;
        } catch (error) {
            console.error("Error guardando proveedor:", error);
            throw error;
        }
    },

    /**
     * Elimina un proveedor.
     * Nota: No borra documentos históricos por seguridad.
     */
    deleteProvider: async (id) => {
        try {
            await deleteDoc(doc(db, PROVIDERS_COL, id));
        } catch (error) {
            console.error("Error eliminando proveedor:", error);
            throw error;
        }
    },

    /**
     * Crea un documento de proveedor (Orden de Compra o Solicitud).
     * Maneja la transacción para generar el folio correlativo único.
     * @param {string} type - 'OC' o 'SC'
     */
    createDocument: async ({ type, provider, items, notes, deliveryDate, total }) => {
        try {
            const counterDocId = type === 'OC' ? 'purchaseOrders' : 'quoteRequests';
            const counterRef = doc(db, COUNTER_COL, counterDocId);

            // 1. Generar Folio Correlativo
            const newId = await runTransaction(db, async (transaction) => {
                const docSnap = await transaction.get(counterRef);
                const count = (docSnap.exists() ? docSnap.data().current : 0) + 1;
                transaction.set(counterRef, { current: count }, { merge: true });
                return count;
            });

            const folio = `${type}-${String(newId).padStart(5, '0')}`;

            // 2. Guardar Documento
            const docData = {
                folio,
                type,
                providerId: provider.id,
                providerName: provider.name,
                items,
                total,
                notes,
                deliveryDate,
                status: 'Emitida',
                createdAt: serverTimestamp(),
                createdBy: 'Sistema' 
            };

            await setDoc(doc(db, PROVIDER_DOCS_COL, folio), docData);
            return folio;

        } catch (error) {
            console.error("Error creando documento de proveedor:", error);
            throw error;
        }
    },

    /**
     * Obtiene los documentos (OC/SC) de un proveedor específico.
     */
    getProviderDocuments: async (providerId) => {
        try {
            const q = query(
                collection(db, PROVIDER_DOCS_COL), 
                where('providerId', '==', providerId), 
                orderBy('createdAt', 'desc')
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (error) {
            console.error("Error obteniendo documentos:", error);
            throw error;
        }
    },

    /**
     * Elimina un documento (OC/SC).
     */
    deleteDocument: async (docId) => {
        try {
            await deleteDoc(doc(db, PROVIDER_DOCS_COL, docId));
        } catch (error) {
            console.error("Error eliminando documento:", error);
            throw error;
        }
    },

    /**
     * Obtiene las facturas registradas en Finanzas asociadas a este proveedor.
     * Busca por nombre exacto del proveedor y tipo 'Factura'.
     */
    getProviderInvoices: async (providerName) => {
        try {
            const q = query(
                collection(db, EXPENSES_COL), 
                where('provider', '==', providerName), 
                where('docType', '==', 'Factura'), 
                orderBy('date', 'desc')
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (error) {
            console.error("Error obteniendo facturas:", error);
            return []; // Retornar vacío en caso de error (ej: falta de índice)
        }
    }
};